import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Sun, Moon, LogOut, User, Settings, Shield, LayoutDashboard, Users, Hammer, FileText, CreditCard, ChevronDown, Images } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { cn } from '../../utils/formatters';

export function Header() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Clientes', href: '/clients', icon: Users },
    { name: 'Proyectos', href: '/projects', icon: Hammer },
    { name: 'Presupuestos', href: '/budgets', icon: FileText },
    { name: 'Gastos', href: '/expenses', icon: CreditCard },
  ];

  const adminNavigation = [
    { name: 'Panel SuperAdmin', href: '/superadmin', icon: Shield },
  ];

  const canManagePortfolio = user?.role === 'admin' || user?.role === 'superadmin';

  const isActive = (href: string) => location.pathname === href || (href !== '/dashboard' && location.pathname.startsWith(href));

  return (
    <header className="sticky top-0 z-40 b2b-topbar safe-area-inset-top safe-area-inset-x">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2" aria-label="ReformAll - Inicio">
              <div className="w-9 h-9 rounded-lg bg-[var(--b2b-accent)] flex items-center justify-center">
                <Hammer className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-secondary-900 dark:text-white hidden sm:block">
                ReformAll
              </span>
            </Link>
          </div>

          <nav className="hidden md:flex items-center gap-1" aria-label="Navegación principal">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive(item.href)
                      ? 'bg-[var(--b2b-accent-tint)] text-[color:var(--b2b-accent-strong)]'
                      : 'text-[color:var(--b2b-text-muted)] hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)]'
                  )}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  <Icon className="w-4 h-4" />
                  {item.name}
                </Link>
              );
            })}
            {canManagePortfolio && (
              <Link
                to="/portfolio"
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                  isActive('/portfolio')
                    ? 'bg-[var(--b2b-accent-tint)] text-[color:var(--b2b-accent-strong)]'
                    : 'text-[color:var(--b2b-text-muted)] hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)]'
                )}
                aria-current={isActive('/portfolio') ? 'page' : undefined}
              >
                <Images className="w-4 h-4" />
                Portfolio
              </Link>
            )}
            {user?.role === 'superadmin' && adminNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive(item.href)
                      ? 'bg-[var(--b2b-slate-800)] text-white dark:bg-[var(--b2b-slate-100)] dark:text-[color:var(--b2b-slate-900)]'
                      : 'text-[color:var(--b2b-text-muted)] hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)]'
                  )}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                >
                  <Icon className="w-4 h-4" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800 transition-colors"
              aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800"
              aria-label={isMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
                aria-label="Menú de usuario"
                aria-expanded={isProfileOpen}
              >
                <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center overflow-hidden">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-primary-700 dark:text-primary-300" />
                  )}
                </div>
                <span className="hidden sm:block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                  {user?.name}
                </span>
                <ChevronDown className="w-4 h-4 text-secondary-500 hidden sm:block" />
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-secondary-200 dark:border-secondary-700 py-1 z-50 animate-in fade-in-0 zoom-in-95">
                  <div className="px-4 py-2 border-b border-secondary-200 dark:border-secondary-700">
                    <p className="text-sm font-medium text-secondary-900 dark:text-white">{user?.name}</p>
                    <p className="text-xs text-secondary-500">{user?.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 rounded-full capitalize">
                      {user?.role}
                    </span>
                  </div>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <User className="w-4 h-4" />
                    Mi Perfil
                  </Link>
                  <Link
                    to="/settings"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700"
                    onClick={() => setIsProfileOpen(false)}
                  >
                    <Settings className="w-4 h-4" />
                    Configuración
                  </Link>
                  <hr className="my-1 border-secondary-200 dark:border-secondary-700" />
                  <button
                    onClick={() => { logout(); setIsProfileOpen(false); }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-secondary-50 dark:hover:bg-secondary-700"
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-secondary-200 dark:border-secondary-800 animate-in slide-in-from-top-2" ref={menuRef}>
            <nav className="flex flex-col gap-1" aria-label="Navegación móvil">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium',
                      isActive(item.href)
                        ? 'bg-[var(--b2b-accent-tint)] text-[color:var(--b2b-accent-strong)]'
                        : 'text-[color:var(--b2b-text-muted)] hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)]'
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              })}
              {canManagePortfolio && (
                <Link
                  to="/portfolio"
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium',
                    isActive('/portfolio')
                      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                      : 'text-[color:var(--b2b-text-muted)] hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)]'
                  )}
                >
                  <Images className="w-5 h-5" />
                  Portfolio
                </Link>
              )}
              {user?.role === 'superadmin' && adminNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-base font-medium',
                      isActive(item.href)
                        ? 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300'
                        : 'text-[color:var(--b2b-text-muted)] hover:bg-[var(--b2b-slate-100)] dark:hover:bg-[var(--b2b-slate-700)]'
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}